import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import Toast from '../Common/Toast';

const isAdminUser = (user) => (
  !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin'
);

const roleLabel = (value) => {
  const v = String(value || '').toLowerCase();
  if (v === 'admin') return 'Администратор';
  if (v === 'user') return 'Пользователь';
  return value || '—';
};

const normalizeApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const chunks = [];
    Object.entries(data).forEach(([field, val]) => {
      if (Array.isArray(val)) chunks.push(`${field}: ${val.join(', ')}`);
      else if (typeof val === 'string') chunks.push(`${field}: ${val}`);
    });
    if (chunks.length) return chunks.join(' | ');
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch (_) {
    return value;
  }
};

const ACTION_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'login', label: 'Вход' },
  { value: 'logout', label: 'Выход' },
  { value: 'create', label: 'Создание' },
  { value: 'update', label: 'Изменение' },
  { value: 'delete', label: 'Удаление' },
];

const hasNetworkMeta = (action) => ['api_call', 'login', 'logout'].includes(String(action || '').toLowerCase());

const UsersList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [savingUserId, setSavingUserId] = useState(null);
  const [toast, setToast] = useState(null);

  const [retentionDays, setRetentionDays] = useState(90);
  const [savingRetention, setSavingRetention] = useState(false);
  const [truncateDateTo, setTruncateDateTo] = useState('');
  const [truncatingLogs, setTruncatingLogs] = useState(false);

  const [logMode, setLogMode] = useState(null); // null | global | user
  const [selectedUser, setSelectedUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [logFilters, setLogFilters] = useState({
    user_id: '',
    action: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [showApiCallsInGlobalLog, setShowApiCallsInGlobalLog] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError('');
    try {
      const [usersResp, uiResp] = await Promise.all([
        apiService.getUsers(),
        apiService.getUiSettings(),
      ]);
      const list = Array.isArray(usersResp?.data) ? usersResp.data : (usersResp?.data?.results || []);
      setUsers(list);
      setRetentionDays(Number(uiResp?.data?.activity_log_retention_days || 90));
    } catch (err) {
      setError(normalizeApiError(err, 'Не удалось загрузить пользователей.'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return users;
    return users.filter((item) => {
      const hay = `${item?.full_name || ''} ${item?.email || ''} ${item?.role || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search]);

  const updateUserField = (userId, patch) => {
    setUsers((prev) => prev.map((x) => (String(x.id) === String(userId) ? { ...x, ...patch } : x)));
  };

  const persistUser = async (row) => {
    setSavingUserId(row.id);
    try {
      const payload = {
        email: row.email,
        full_name: row.full_name,
        role: row.role,
        is_active: !!row.is_active,
        is_staff: !!row.is_staff,
        can_select_etp_host: !!row.can_select_etp_host,
      };
      const resp = await apiService.updateUser(row.id, payload);
      const next = resp?.data || row;
      updateUserField(row.id, next);
      showToast('Пользователь сохранен');
    } catch (err) {
      showToast(normalizeApiError(err, 'Ошибка при сохранении пользователя.'), 'error');
      await loadUsers();
    } finally {
      setSavingUserId(null);
    }
  };

  const saveRetentionDays = async () => {
    setSavingRetention(true);
    try {
      const normalized = Math.max(1, Math.min(3650, Number(retentionDays || 90)));
      await apiService.saveUiSettings({ activity_log_retention_days: normalized });
      setRetentionDays(normalized);
      showToast('Срок хранения журнала обновлен');
    } catch (err) {
      showToast(normalizeApiError(err, 'Ошибка при сохранении срока хранения журнала.'), 'error');
    } finally {
      setSavingRetention(false);
    }
  };

  const truncateLogsBeforeDate = async () => {
    if (!truncateDateTo) {
      showToast('Выберите дату для обрезки журнала.', 'error');
      return;
    }
    const ok = window.confirm(`Удалить все записи общего журнала по ${truncateDateTo} включительно?`);
    if (!ok) return;

    setTruncatingLogs(true);
    try {
      const resp = await apiService.truncateActivityLogsBefore(truncateDateTo);
      const deletedCount = Number(resp?.data?.deleted_count || 0);
      showToast(`Журнал обрезан. Удалено записей: ${deletedCount}.`);
      if (logMode === 'global') {
        await loadLogs('global', null);
      }
    } catch (err) {
      showToast(normalizeApiError(err, 'Не удалось обрезать общий журнал.'), 'error');
    } finally {
      setTruncatingLogs(false);
    }
  };

  const buildLogsQuery = (modeArg = logMode) => {
    const params = new URLSearchParams();
    if (logFilters.action) params.set('action', logFilters.action);
    if (logFilters.search) params.set('search', logFilters.search);
    if (logFilters.date_from) params.set('date_from', logFilters.date_from);
    if (logFilters.date_to) params.set('date_to', logFilters.date_to);
    if (modeArg === 'global' && logFilters.user_id) params.set('user_id', String(logFilters.user_id));
    if (modeArg === 'global' && showApiCallsInGlobalLog) params.set('include_api_calls', '1');
    return `?${params.toString()}`;
  };

  const loadLogs = async (modeArg = logMode, userArg = selectedUser) => {
    if (!modeArg) return;
    setLoadingLogs(true);
    try {
      const query = buildLogsQuery(modeArg);
      const resp = modeArg === 'user'
        ? await apiService.getUserActivityLogs(userArg?.id, query)
        : await apiService.getActivityLogs(query);
      const rawList = Array.isArray(resp?.data) ? resp.data : (resp?.data?.results || []);
      const list = (modeArg === 'global' && !showApiCallsInGlobalLog)
        ? rawList.filter((item) => String(item?.action || '').toLowerCase() !== 'api_call')
        : rawList;
      setLogs(list);
    } catch (err) {
      showToast(normalizeApiError(err, 'Не удалось загрузить журнал действий.'), 'error');
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadUserLogs = async (row) => {
    setLogMode('user');
    setSelectedUser(row);
    setLogFilters((prev) => ({ ...prev, user_id: String(row.id) }));
    setLogs([]);
    await loadLogs('user', row);
  };

  const loadGlobalLogs = async () => {
    setLogMode('global');
    setSelectedUser(null);
    setLogs([]);
    await loadLogs('global', null);
  };

  const applyLogFilters = async () => {
    await loadLogs();
  };

  const resetLogFilters = async () => {
    const fallbackUserId = logMode === 'user' ? String(selectedUser?.id || '') : '';
    setLogFilters({ user_id: fallbackUserId, action: '', search: '', date_from: '', date_to: '' });
    setTimeout(() => { loadLogs(); }, 0);
  };

  useEffect(() => {
    if (logMode !== 'global' && showApiCallsInGlobalLog) {
      setShowApiCallsInGlobalLog(false);
    }
  }, [logMode, showApiCallsInGlobalLog]);

  useEffect(() => {
    if (logMode !== 'global') return;
    if (logFilters.action === 'api_call') {
      setLogFilters((prev) => ({ ...prev, action: '' }));
    }
  }, [logMode, logFilters.action]);

  useEffect(() => {
    if (logMode !== 'global') return;
    loadLogs('global', null);
  }, [showApiCallsInGlobalLog]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin) {
    return <div style={{ padding: 24 }}>Доступ запрещен.</div>;
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Загрузка пользователей…</div>;
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Пользователи</h1>
        <input
          type="text"
          placeholder="Поиск по ФИО, email, роли"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 320 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <span>Хранить журнал действий (дней):</span>
        <input
          type="number"
          min={1}
          max={3650}
          value={retentionDays}
          onChange={(e) => setRetentionDays(e.target.value)}
          style={{ width: 110 }}
          disabled={savingRetention}
        />
        <button type="button" onClick={saveRetentionDays} disabled={savingRetention}>
          {savingRetention ? 'Сохранение…' : 'Сохранить'}
        </button>
        <button type="button" onClick={loadGlobalLogs}>Общий журнал</button>
        <span style={{ marginLeft: 10 }}>Обрезать общий журнал по дату:</span>
        <input
          type="date"
          value={truncateDateTo}
          onChange={(e) => setTruncateDateTo(e.target.value)}
          disabled={truncatingLogs}
        />
        <button type="button" onClick={truncateLogsBeforeDate} disabled={truncatingLogs || !truncateDateTo}>
          {truncatingLogs ? 'Обрезка…' : 'Обрезать'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fff3f3', border: '1px solid #ffc9c9', color: '#c92a2a', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e9ecef', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid #e9ecef' }}>Пользователь</th>
              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid #e9ecef' }}>Email</th>
              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid #e9ecef' }}>Роль</th>
              <th style={{ textAlign: 'center', padding: 12, borderBottom: '1px solid #e9ecef' }}>Активен</th>
              <th style={{ textAlign: 'center', padding: 12, borderBottom: '1px solid #e9ecef' }}>Админ-доступ</th>
              <th style={{ textAlign: 'center', padding: 12, borderBottom: '1px solid #e9ecef' }}>Выбор хоста ЭТП</th>
              <th style={{ textAlign: 'center', padding: 12, borderBottom: '1px solid #e9ecef' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#6c757d' }}>Пользователи не найдены</td></tr>
            )}
            {filteredUsers.map((row) => {
              const isSaving = String(savingUserId) === String(row.id);
              return (
                <tr key={row.id}>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5' }}>{row.full_name || '—'}</td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5' }}>{row.email || '—'}</td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5' }}>{roleLabel(row.role)}</td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5', textAlign: 'center' }}><input type="checkbox" checked={!!row.is_active} onChange={(e) => updateUserField(row.id, { is_active: !!e.target.checked })} disabled={isSaving} /></td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5', textAlign: 'center' }}><input type="checkbox" checked={!!row.is_staff} onChange={(e) => { const checked = !!e.target.checked; updateUserField(row.id, { is_staff: checked, role: checked ? 'admin' : 'user' }); }} disabled={isSaving || !!row.is_superuser} title={row.is_superuser ? 'Суперпользователь всегда администратор' : ''} /></td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5', textAlign: 'center' }}><input type="checkbox" checked={!!row.can_select_etp_host} onChange={(e) => updateUserField(row.id, { can_select_etp_host: !!e.target.checked })} disabled={isSaving} /></td>
                  <td style={{ padding: 12, borderBottom: '1px solid #f1f3f5', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                      <button type="button" onClick={() => persistUser(row)} disabled={isSaving}>{isSaving ? 'Сохранение…' : 'Сохранить'}</button>
                      <button type="button" onClick={() => loadUserLogs(row)}>Журнал</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {logMode && (
        <div style={{ marginTop: 20, background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: 10, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{logMode === 'global' ? 'Общий журнал действий' : `Журнал действий: ${selectedUser?.full_name || selectedUser?.email}`}</h3>
            <button type="button" onClick={() => { setLogMode(null); setSelectedUser(null); }}>Закрыть</button>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {logMode === 'global' && (
              <select value={logFilters.user_id} onChange={(e) => setLogFilters((prev) => ({ ...prev, user_id: e.target.value }))}>
                <option value="">Все пользователи</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
              </select>
            )}
            {logMode === 'global' && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                <input
                  type="checkbox"
                  checked={showApiCallsInGlobalLog}
                  onChange={(e) => setShowApiCallsInGlobalLog(!!e.target.checked)}
                />
                API-вызовы
              </label>
            )}
            <select value={logFilters.action} onChange={(e) => setLogFilters((prev) => ({ ...prev, action: e.target.value }))}>
              {(showApiCallsInGlobalLog ? [...ACTION_OPTIONS.slice(0, 3), { value: 'api_call', label: 'API вызов' }, ...ACTION_OPTIONS.slice(3)] : ACTION_OPTIONS)
                .map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input type="date" value={logFilters.date_from} onChange={(e) => setLogFilters((prev) => ({ ...prev, date_from: e.target.value }))} />
            <input type="date" value={logFilters.date_to} onChange={(e) => setLogFilters((prev) => ({ ...prev, date_to: e.target.value }))} />
            <input
              type="text"
              placeholder="Поиск по сущности"
              value={logFilters.search}
              onChange={(e) => setLogFilters((prev) => ({ ...prev, search: e.target.value }))}
              style={{ minWidth: 220 }}
            />
            <button type="button" onClick={applyLogFilters}>Применить</button>
            <button type="button" onClick={resetLogFilters}>Сбросить</button>
          </div>

          {loadingLogs ? (
            <div style={{ color: '#6c757d' }}>Загрузка журнала…</div>
          ) : (
            <>
              <div style={{ overflow: 'auto', maxHeight: '70vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>Время</th>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>Пользователь</th>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>Действие</th>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>Сущность</th>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>Статус</th>
                      <th style={{ textAlign: 'left', padding: 5, borderBottom: '1px solid #e9ecef' }}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 && <tr><td colSpan={6} style={{ padding: 12, textAlign: 'center', color: '#6c757d' }}>Записей пока нет</td></tr>}
                    {logs.map((item) => (
                      <tr key={item.id}>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>{formatDateTime(item.created_at)}</td>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>{item.actor_name || '—'}</td>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>{item.action_label || item.action}</td>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>{item.object_repr || item.entity_label || '—'}</td>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>
                          {hasNetworkMeta(item.action) ? (item.status_code || '—') : '—'}
                        </td>
                        <td style={{ padding: 5, borderBottom: '1px solid #f1f3f5' }}>
                          {hasNetworkMeta(item.action) ? (item.ip_address || '—') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default UsersList;
